import { Component, OnInit } from "@angular/core";
import { AppConfigService } from "./app-config.service";
import { AppConfigType } from "../shared/models/appConfig.model";
import { MatSnackBar } from "@angular/material/snack-bar";
import { TranslateService } from "@ngx-translate/core";

@Component({
    selector: "app-config",
    templateUrl: "./app-config.component.html",
    styleUrls: ["./app-config.component.scss"],
    standalone: false,
})
export class AppConfigComponent implements OnInit {
    protected readonly appConfigType = AppConfigType;

    public readonly HOURS_PATTERN = /^\d+:[0-5][0-9]$/;

    private readonly DEFAULT_WARNINGS_THRESHOLDS = {
        days: 2,
        hours: "03:00",
        cycles: 50,
        flightNumber: 5,
    };

    private readonly DEFAULT_ENGINES_FORM = {
        cycles: false,
        drivetrain: false,
        hydraulics: false,
    };

    private readonly DEFAULT_REFUEL_LOCATION = {
        camo: false,
        pilot: false,
        maintenance: false,
        label: "location",
    };

    private readonly DEFAULT_HOOK = {
        accessories: false,
        cycles: false,
        hours: false,
        label: "cycles",
    };

    private readonly DEFAULT_HOIST = {
        accessories: false,
        cycles: false,
        hours: false,
        inspections: false,
    };

    private readonly DEFAULT_ENGINE_POWER_CHECK = {
        automatic: false,
    };

    private readonly DEFAULT_FLIGHT_EDITING = {
        hours: "24:00",
    };

    private readonly DEFAULT_CREW = {
        dailyPrefill: false,
    };

    private readonly DEFAULT_PREFILL_COPILOT_DIRECT_FLIGHT = {
        dailyPrefill: false,
    };

    private readonly DEFAULT_NUMPAD_IOS_26_ENABLED = true;

    private readonly DEFAULT_OIL_UNITS = {
        ml: true,
        lt: true,
        qt: false,
    };

    private readonly DEFAULT_FUEL_UNITS = {
        kg: true,
        lt: true,
    };

    private readonly DEFAULT_FLIGHT_CARD = {
        theme: "default",
    };

    private readonly DEFAULT_WARNING_LONG_DURATION = {
        aircraft: "12:00",
        helicopter: "06:00",
    };

    private readonly DEFAULT_AC_CYCLES = {
        prefill: false,
    };

    private readonly DEFAULT_AUTO_LOCK = {
        foregroundTimeoutSeconds: 180,
        backgroundGraceSeconds: 20,
        lockOnDeviceLock: false,
    };

    readonly AUTO_LOCK_LIMITS = {
        foregroundTimeoutSeconds: { min: 30, max: 900 },
        backgroundGraceSeconds: { min: 10, max: 300 },
    };

    gridColumns: { title: string; field: any }[] = [];
    appConfigKeys: number[] = [];

    constructor(
        private appConfigService: AppConfigService,
        private _snackBar: MatSnackBar,
        private _translate: TranslateService,
    ) {
        this.gridColumns.push({ title: "", field: "" });
    }

    ngOnInit() {
        this.appConfigService.getAll().subscribe((orgs: any[]) => {
            this.appConfigKeys = Object.keys(AppConfigType)
                .filter((key) => !isNaN(Number(key)))
                .map((key) => Number(key));
            orgs.map((org) => {
                this.gridColumns.push({
                    title: org.name,
                    field: {
                        ...org,
                        appConfigs: this.appConfigKeys.map((key) => {
                            const orgAppConfig: any = org.appConfigs.find((a) => a.type === key);
                            if (orgAppConfig) {
                                if (key === AppConfigType.inactivityTime) {
                                    const [hours, minutes] = this.splitTimeValue(orgAppConfig.value);
                                    orgAppConfig.valueHours = hours;
                                    orgAppConfig.valueMinutes = minutes;
                                }
                                if (key === AppConfigType.passengers) {
                                    const [first, second, unit] = this.splitPassengersValue(orgAppConfig.value);
                                    orgAppConfig.valueFirst = first;
                                    orgAppConfig.valueSecond = second;
                                    orgAppConfig.valueUnit = unit;
                                }
                                if (
                                    key === AppConfigType.shortFrequencyWarnings ||
                                    key === AppConfigType.holdItemListWarnings ||
                                    key === AppConfigType.nextScheduleWarning ||
                                    key == AppConfigType.dailyCheck
                                ) {
                                    if (orgAppConfig.value) {
                                        orgAppConfig.value = JSON.parse(orgAppConfig.value);
                                    } else {
                                        orgAppConfig.value = Object.create(this.DEFAULT_WARNINGS_THRESHOLDS);
                                        if (key === AppConfigType.nextScheduleWarning) {
                                            delete orgAppConfig.value.cycles;
                                            delete orgAppConfig.value.flightNumber;
                                        }
                                        if (key === AppConfigType.dailyCheck) {
                                            orgAppConfig.value = { hours: "01:00" };
                                        }
                                    }
                                }
                                if (key === AppConfigType.enginesForm) {
                                    if (orgAppConfig.value) {
                                        orgAppConfig.value = JSON.parse(orgAppConfig.value);
                                    } else {
                                        orgAppConfig.value = Object.create(this.DEFAULT_ENGINES_FORM);
                                    }
                                }
                                if (key === AppConfigType.flightRefuelLocation) {
                                    if (orgAppConfig.value) {
                                        orgAppConfig.value = JSON.parse(orgAppConfig.value);
                                    } else {
                                        orgAppConfig.value = Object.create(this.DEFAULT_REFUEL_LOCATION);
                                    }
                                }
                                if (key === AppConfigType.hooks) {
                                    if (orgAppConfig.value) {
                                        orgAppConfig.value = JSON.parse(orgAppConfig.value);
                                    } else {
                                        orgAppConfig.value = Object.create(this.DEFAULT_HOOK);
                                    }
                                }
                                if (key === AppConfigType.hoist) {
                                    if (orgAppConfig.value) {
                                        orgAppConfig.value = JSON.parse(orgAppConfig.value);
                                    } else {
                                        orgAppConfig.value = Object.create(this.DEFAULT_HOIST);
                                    }
                                }
                                if (key === AppConfigType.enginePowerCheck) {
                                    if (orgAppConfig.value) {
                                        orgAppConfig.value = JSON.parse(orgAppConfig.value);
                                    } else {
                                        orgAppConfig.value = Object.create(this.DEFAULT_ENGINE_POWER_CHECK);
                                    }
                                }
                                if (key === AppConfigType.flightEditing) {
                                    if (orgAppConfig.value) {
                                        orgAppConfig.value = JSON.parse(orgAppConfig.value);
                                    } else {
                                        orgAppConfig.value = Object.create(this.DEFAULT_FLIGHT_EDITING);
                                    }
                                }
                                if (key === AppConfigType.crew) {
                                    if (orgAppConfig.value) {
                                        orgAppConfig.value = JSON.parse(orgAppConfig.value);
                                    } else {
                                        orgAppConfig.value = Object.create(this.DEFAULT_CREW);
                                    }
                                }
                                if (key === AppConfigType.prefillCopilotDirectFlights) {
                                    if (orgAppConfig.value) {
                                        orgAppConfig.value = JSON.parse(orgAppConfig.value);
                                    } else {
                                        orgAppConfig.value = Object.create(this.DEFAULT_PREFILL_COPILOT_DIRECT_FLIGHT);
                                    }
                                }
                                if (key === AppConfigType.numpadIOS26) {
                                    if (orgAppConfig.enabled !== true && orgAppConfig.enabled !== false) {
                                        orgAppConfig.enabled = this.DEFAULT_NUMPAD_IOS_26_ENABLED;
                                    }
                                }
                                if (key === AppConfigType.oilUnits) {
                                    if (orgAppConfig.value) {
                                        orgAppConfig.value = JSON.parse(orgAppConfig.value);
                                    } else {
                                        orgAppConfig.value = Object.create(this.DEFAULT_OIL_UNITS);
                                    }
                                }
                                if (key === AppConfigType.fuelUnits) {
                                    if (orgAppConfig.value) {
                                        orgAppConfig.value = JSON.parse(orgAppConfig.value);
                                    } else {
                                        orgAppConfig.value = Object.create(this.DEFAULT_FUEL_UNITS);
                                    }
                                }
                                if (key === AppConfigType.flightCard) {
                                    if (orgAppConfig.value) {
                                        orgAppConfig.value = JSON.parse(orgAppConfig.value);
                                    } else {
                                        orgAppConfig.value = Object.create(this.DEFAULT_FLIGHT_CARD);
                                    }
                                }
                                if (key === AppConfigType.warningLongDuration) {
                                    if (orgAppConfig.value) {
                                        orgAppConfig.value = JSON.parse(orgAppConfig.value);
                                    } else {
                                        orgAppConfig.value = Object.create(this.DEFAULT_WARNING_LONG_DURATION);
                                    }
                                }
                                if (key === AppConfigType.acCycles) {
                                    if (orgAppConfig.value) {
                                        orgAppConfig.value = JSON.parse(orgAppConfig.value);
                                    } else {
                                        orgAppConfig.value = { ...this.DEFAULT_AC_CYCLES };
                                    }
                                }
                                if (key === AppConfigType.autoLock) {
                                    orgAppConfig.enabled = true;
                                    orgAppConfig.value = { ...this.DEFAULT_AUTO_LOCK, ...(orgAppConfig.value ? JSON.parse(orgAppConfig.value) : {}) };
                                }
                                orgAppConfig.orgId = org.id;
                                return orgAppConfig;
                            }
                            const defaultAppConfig: any = {
                                orgId: org.id,
                                type: key,
                                enabled: false,
                                value: null,
                            };
                            if (key === AppConfigType.inactivityTime) {
                                defaultAppConfig.valueHours = null;
                                defaultAppConfig.valueMinutes = null;
                            }
                            if (key === AppConfigType.passengers) {
                                defaultAppConfig.valueFirst = 0;
                                defaultAppConfig.valueSecond = null;
                                defaultAppConfig.valueUnit = null;
                            }
                            if (key === AppConfigType.shortFrequencyWarnings || key === AppConfigType.holdItemListWarnings || key === AppConfigType.nextScheduleWarning) {
                                defaultAppConfig.value = Object.create(this.DEFAULT_WARNINGS_THRESHOLDS);
                                if (key === AppConfigType.nextScheduleWarning) {
                                    defaultAppConfig.value.days = 10;
                                    defaultAppConfig.value.hours = "10:00";
                                    delete defaultAppConfig.value.cycles;
                                    delete defaultAppConfig.value.flightNumber;
                                }
                            }
                            if (key === AppConfigType.dailyCheck) {
                                defaultAppConfig.value = { hours: "01:00" };
                            }
                            if (key === AppConfigType.enginesForm) {
                                defaultAppConfig.value = Object.create(this.DEFAULT_ENGINES_FORM);
                            }
                            if (key === AppConfigType.hooks) {
                                defaultAppConfig.value = Object.create(this.DEFAULT_HOOK);
                            }
                            if (key === AppConfigType.hoist) {
                                defaultAppConfig.value = Object.create(this.DEFAULT_HOIST);
                            }
                            if (key === AppConfigType.flightRefuelLocation) {
                                defaultAppConfig.value = Object.create(this.DEFAULT_REFUEL_LOCATION);
                            }
                            if (key === AppConfigType.enginePowerCheck) {
                                defaultAppConfig.value = Object.create(this.DEFAULT_ENGINE_POWER_CHECK);
                            }
                            if (key === AppConfigType.flightEditing) {
                                defaultAppConfig.value = Object.create(this.DEFAULT_FLIGHT_EDITING);
                            }
                            if (key === AppConfigType.crew) {
                                defaultAppConfig.value = Object.create(this.DEFAULT_CREW);
                            }
                            if (key === AppConfigType.prefillCopilotDirectFlights) {
                                defaultAppConfig.value = Object.create(this.DEFAULT_PREFILL_COPILOT_DIRECT_FLIGHT);
                            }
                            if (key == AppConfigType.numpadIOS26) {
                                defaultAppConfig.enabled = this.DEFAULT_NUMPAD_IOS_26_ENABLED;
                            }
                            if (key === AppConfigType.oilUnits) {
                                defaultAppConfig.value = Object.create(this.DEFAULT_OIL_UNITS);
                            }
                            if (key === AppConfigType.fuelUnits) {
                                defaultAppConfig.value = Object.create(this.DEFAULT_FUEL_UNITS);
                            }
                            if (key === AppConfigType.flightCard) {
                                defaultAppConfig.value = Object.create(this.DEFAULT_FLIGHT_CARD);
                            }
                            if (key === AppConfigType.warningLongDuration) {
                                defaultAppConfig.value = Object.create(this.DEFAULT_WARNING_LONG_DURATION);
                            }
                            if (key === AppConfigType.acCycles) {
                                defaultAppConfig.value = { ...this.DEFAULT_AC_CYCLES };
                            }
                            if (key === AppConfigType.autoLock) {
                                defaultAppConfig.enabled = true;
                                defaultAppConfig.value = { ...this.DEFAULT_AUTO_LOCK };
                            }
                            return defaultAppConfig;
                        }),
                    },
                });
            });
        });
    }

    private splitPassengersValue(value: string): [number, number, string] {
        if (!value) {
            return [0, null, ""];
        }

        const parts = value.split("-");
        const first = parseInt(parts[0], 10) || 0;
        const second = parseInt(parts[1], 10) || null;
        const unit = parts[2] || "";

        return [first, second, unit];
    }

    onPassengersValueChange(appConfig: any): void {
        const first = appConfig.valueFirst ? 1 : 0;
        const second = appConfig.valueSecond || "";
        const unit = appConfig.valueUnit || "";

        if (first === 1 && !appConfig.enabled) appConfig.enabled = true;

        appConfig.value = second && unit ? `${first}-${second}-${unit}` : `${first}`;
    }

    onInactivityValueChange(appConfig: any): void {
        const hours = appConfig.valueHours || 0;
        const minutes = appConfig.valueMinutes || 0;

        appConfig.value = `${this.formatTwoDigits(hours)}:${this.formatTwoDigits(minutes)}`;
    }

    private splitTimeValue(value: string): [number, number] {
        if (!value || !value.includes(":")) {
            return [0, 0];
        }

        const [hours, minutes] = value.split(":").map((part) => parseInt(part, 10));
        return [hours || 0, minutes || 0];
    }

    private formatTwoDigits(value: number): string {
        return value < 10 ? `0${value}` : `${value}`;
    }

    getEnumKeyByValue(value: number): string {
        return Object.keys(AppConfigType).find((key) => AppConfigType[key as keyof typeof AppConfigType] === value) || "Unknown";
    }

    preventNonIntegerKey(event: KeyboardEvent): void {
        if ([".", ",", "e", "E", "+", "-"].includes(event.key)) event.preventDefault();
    }

    private toWholeSeconds(value: any, limits: { min: number; max: number }, fallback: number): number {
        const seconds = Math.trunc(Number(value));
        if (value === null || value === "" || !Number.isFinite(seconds)) return fallback;
        return Math.min(Math.max(seconds, limits.min), limits.max);
    }

    onCrewValueChange(appConfig: any) {
        if (appConfig.enabled) {
            const orgs = this.gridColumns.slice(1);
            const orgField = orgs.find((o) => o.field.id === appConfig.orgId).field;
            const orgAppConfigs = orgField.appConfigs;
            const orgPassengersAppConfig = orgAppConfigs.find((a) => a.type === AppConfigType.passengers);
            orgPassengersAppConfig.enabled = true;
        }
    }

    submit() {
        const updatedAppConfigs = this.gridColumns.slice(1).map((gc) => ({
            orgId: gc.field.id,
            appConfigs: gc.field.appConfigs.map((a) => {
                let value: any = a.value;
                if (
                    a.type == AppConfigType.shortFrequencyWarnings ||
                    a.type == AppConfigType.nextScheduleWarning ||
                    a.type == AppConfigType.holdItemListWarnings ||
                    a.type == AppConfigType.dailyCheck ||
                    a.type == AppConfigType.enginesForm
                ) {
                    value = JSON.stringify(a.value);
                }
                if (a.type == AppConfigType.autoLock) {
                    value = {
                        ...a.value,
                        foregroundTimeoutSeconds: this.toWholeSeconds(
                            a.value.foregroundTimeoutSeconds,
                            this.AUTO_LOCK_LIMITS.foregroundTimeoutSeconds,
                            this.DEFAULT_AUTO_LOCK.foregroundTimeoutSeconds,
                        ),
                        backgroundGraceSeconds: this.toWholeSeconds(
                            a.value.backgroundGraceSeconds,
                            this.AUTO_LOCK_LIMITS.backgroundGraceSeconds,
                            this.DEFAULT_AUTO_LOCK.backgroundGraceSeconds,
                        ),
                    };
                }
                return {
                    enabled: a.enabled,
                    value: value,
                    type: a.type,
                };
            }),
        }));

        this.appConfigService.updateAll(updatedAppConfigs).subscribe({
            next: () => {
                window.location.reload();
                this._snackBar.open(this._translate.instant("APPCONFIG.success"), this._translate.instant("GENERAL.close"), {
                    duration: 3000,
                });
            },
            error: (e) => {
                this._snackBar.open(this._translate.instant("APPCONFIG.error"), this._translate.instant("GENERAL.close"), {
                    duration: 3000,
                });
            },
        });
    }

    onPilotCheckboxChange(event, appConfig) {
        if (event.checked) {
            appConfig.value.pilot = { canUpdate: false, canClose: false };
        } else {
            delete appConfig.value.pilot;
        }
    }

    onPilotSubSwitchChange(event, appConfig, type: number) {
        switch (type) {
            case 1:
                appConfig.value.pilot.canUpdate = event.checked;
                break;
            case 2:
                appConfig.value.pilot.canClose = event.checked;
                break;
        }
    }
}
